import { useCallback, useMemo, useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useAiContextStore } from "@/store/zustand/aiContextStore";
import { useSelectedNavTreeNode } from "@/components/Canvas/stores/canvasStore";
import { TempoElement } from "@/utils/tempoElement";
import { Sparkles, ArrowUp } from "lucide-react";
import { findElementByDomUniquePath } from "@/components/Canvas/utils/utils";
import useLayoutStore, { LeftTab } from "@/store/zustand/layoutStore";

interface AITextEditButtonProps {
  color: string;
  iframeRef?: React.RefObject<HTMLIFrameElement>;
}

export const AITextEditButton = ({
  color,
  iframeRef,
}: AITextEditButtonProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputText, setInputText] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const { selectedNavTreeNode, selectedElement } = useSelectedNavTreeNode();

  const treeElementLookup = useCodebaseStore(
    (state) => state.treeElementLookup,
  );
  const setSendChatMessage = useAiContextStore(
    (state) => state.setSendChatMessage,
  );
  const setNewLayout = useLayoutStore((state) => state.setNewLayout);

  // Get text content from DOM element
  const getTextContentFromDOM = useCallback(() => {
    if (!iframeRef?.current) return "";
    if (!selectedNavTreeNode) return "";

    try {
      const iframeDoc =
        iframeRef.current?.contentDocument ||
        iframeRef.current?.contentWindow?.document;
      if (!iframeDoc) return "";

      const element = findElementByDomUniquePath(
        iframeDoc,
        selectedNavTreeNode.domUniquePath ??
          selectedNavTreeNode.domUniquePathForComponent,
      );
      if (element) {
        const textNodes = Array.from(element.childNodes).filter(
          (node) => node.nodeType === Node.TEXT_NODE,
        );

        if (textNodes?.length > 0) {
          return element.textContent?.trim() || "";
        }

        return "";
      }
    } catch (e) {
      // Iframe might not be accessible due to CORS
      return "";
    }

    return "";
  }, [selectedNavTreeNode, iframeRef]);

  // Check if the selected element has text content but cannot be directly edited
  const shouldShowButton = useMemo(() => {
    if (selectedElement.isEmpty() || !selectedNavTreeNode) return false;

    const treeElement = treeElementLookup[selectedElement.codebaseId];
    if (!treeElement) return false;

    // Element cannot be directly edited (staticTextContents is false or undefined)
    const cannotDirectEdit = !treeElement.staticTextContents;

    // Get actual text content from DOM
    const textContent = getTextContentFromDOM();
    const hasTextContent = textContent.length > 0;

    return hasTextContent && cannotDirectEdit;
  }, [
    selectedElement,
    selectedNavTreeNode,
    treeElementLookup,
    getTextContentFromDOM,
  ]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  // Reset editing state when element changes
  useEffect(() => {
    setIsEditing(false);
    setInputText("");
  }, [selectedElement.getKey()]);

  const handleButtonClick = useCallback(() => {
    if (!shouldShowButton) return;
    setIsEditing(true);
  }, [shouldShowButton]);

  const handleSubmit = useCallback(() => {
    if (!inputText.trim()) return;

    const currentText = getTextContentFromDOM();
    if (!currentText) return;

    // Create AI message to edit the text
    const aiMessage = `Please change the text "${currentText}" to "${inputText.trim()}" for the selected element.`;

    const sent = setSendChatMessage({
      sendChatMessage: aiMessage,
      sendChatMessageFiles: undefined,
    });

    if (sent) {
      // Open AI chat panel and reset state
      setNewLayout(LeftTab.AIChat);
      setIsEditing(false);
      setInputText("");
    }
  }, [inputText, getTextContentFromDOM, setSendChatMessage, setNewLayout]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      } else if (e.key === "Escape") {
        setIsEditing(false);
        setInputText("");
      }
    },
    [handleSubmit],
  );

  if (!shouldShowButton) {
    return null;
  }

  if (isEditing) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "2px",
        }}
      >
        <input
          ref={inputRef}
          type="text"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter new text..."
          className="ai-text-edit-input"
          style={{
            backgroundColor: "#2a2d35", // Dark background to match the theme
            color: "white", // White text
            border: "1px solid #4a5568", // Subtle border
            borderRadius: "3px",
            padding: "2px 6px",
            fontSize: "10px",
            fontWeight: "normal",
            width: "140px",
            outline: "none",
            boxSizing: "border-box",
          }}
        />
        <button
          onClick={handleSubmit}
          disabled={!inputText.trim()}
          style={{
            color: "white",
            border: "none",
            borderRadius: "3px",
            cursor: inputText.trim() ? "pointer" : "not-allowed",
            padding: "2px 6px",
            fontSize: "11px",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            opacity: inputText.trim() ? 1 : 0.5,
            boxSizing: "border-box",
            height: "100%",
          }}
          className="bg-primary-n-500"
          title="Send"
        >
          <ArrowUp style={{ width: "10px", height: "10px" }} />
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={handleButtonClick}
      style={{
        backgroundColor: color,
        color: "white",
        padding: "2px 6px",
        borderRadius: "3px",
        fontSize: "11px",
        fontWeight: "bold",
        whiteSpace: "nowrap",
        display: "flex",
        alignItems: "center",
        gap: "2px",
        border: "none",
        cursor: "pointer",
      }}
      title="Change text with AI"
    >
      <Sparkles style={{ width: "10px", height: "10px" }} />
      Change text with AI
    </button>
  );
};
